/*-----------------------------------------------------------------------------/
/ EXC shipping cutoff countdown
/
/ <shipping-countdown> renders the time left until the store's same day
/ shipping cutoff. Before the cutoff it counts down to today's cutoff, after it
/ counts down to the next day's cutoff, and it swaps the wording between the
/ two. All of it comes from data attributes set by the Price block settings
/ (snippets/pdp-blocks.liquid), so the merchant edits the cutoff hour, the
/ timezone and both sentences in the theme editor.
/
/ The cutoff is worked out in the store's timezone, not the shopper's, so a
/ rider in California sees the same deadline as one in New York.
/ Notes: hq/projects/excdesigns/theme-build-notes.md
/-----------------------------------------------------------------------------*/
class ShippingCountdown extends HTMLElement {
  connectedCallback() {
    this.output = this.querySelector('[data-countdown-text]');
    this.cutoffHour = parseInt(this.dataset.cutoffHour, 10);
    this.timeZone = this.dataset.timezone || 'America/New_York';
    this.textBefore = this.dataset.textBefore || '{time}';
    this.textAfter = this.dataset.textAfter || '{time}';

    if (!this.output || isNaN(this.cutoffHour)) return;

    this.render();
    // A minute is enough for an hours:minutes readout, and it keeps the
    // wording flipping over within a minute of the cutoff passing.
    this.timer = setInterval(() => this.render(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  // Seconds elapsed today in the store's timezone
  #storeSecondsNow() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timeZone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(new Date());

    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    // Intl can report hour 24 for midnight in some engines
    const hour = get('hour') % 24;
    return hour * 3600 + get('minute') * 60 + get('second');
  }

  render() {
    const now = this.#storeSecondsNow();
    const cutoff = this.cutoffHour * 3600;
    const beforeCutoff = now < cutoff;
    let remaining = cutoff - now;
    if (!beforeCutoff) remaining += 86400;

    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    // "14h 11m", and just "45m" in the last hour so it does not read "0h 45m"
    const time = hours > 0
      ? `${hours}h ${String(minutes).padStart(2, '0')}m`
      : `${minutes}m`;

    const template = beforeCutoff ? this.textBefore : this.textAfter;
    this.output.textContent = template.replace('{time}', time);
    this.dataset.state = beforeCutoff ? 'before-cutoff' : 'after-cutoff';
    this.hidden = false;
  }
}

if (!customElements.get('shipping-countdown')) {
  customElements.define('shipping-countdown', ShippingCountdown);
}
